// Builds runtime Unit instances from RunState roster + enemy definitions.
// unit.pos uses slot-coords: {q: slot (0-4), r: 0=player / 1=enemy}.

import { CHAMPIONS, type ChampionId, type ChampionTier } from "../data/champions";
import { ENEMIES, type EnemyId } from "../data/enemies";
import { ITEMS } from "../data/items";
import { championIconUrl } from "../config/constants";
import type { RosterEntry, StarLevel } from "../game/RunState";
import { effectiveStats } from "../units/UnitStats";
import { nextUnitId, type Unit, type UnitKindDef, type Team } from "../units/Unit";
import type { HexCoord } from "../engine/hex/HexCoord";

function slotCoord(slot: number, team: Team): HexCoord {
  return { q: slot, r: team === "player" ? 0 : 1 };
}

function freshCombatState(abilityCooldown: number) {
  return {
    damageDealt: 0,
    cooldownTurns: abilityCooldown,
    autoAttackCount: 0,
    stunTurnsRemaining: 0,
    stasisTurnsRemaining: 0,
    sleepTurnsRemaining: 0,
    shieldAmount: 0,
    damageReductionPercent: 0,
    damageReductionTurnsRemaining: 0,
    olafUltTurnsRemaining: 0,
    alphaStrikeTurnsRemaining: 0,
    tibbersUnitId: null,
    jaxCounterStrikeTurnsRemaining: 0,
    jaxCounterStrikeBlockedCount: 0,
    abilityCastCount: 0,
    passiveStacks: 0,
    bleedTickDamage: 0,
    bleedTurnsRemaining: 0,
    jayceHammerForm: false,
    currentTarget: null,
    redBuff: false,
    blueBuff: false,
    yuumiBoostMult: 1,
    item: null,
    bansheeActive: false,
    healReductionPercent: 0,
    healReductionTurnsRemaining: 0,
    warmogRegenAccum: 0,
    vayneStacks: 0,
  };
}

function getStarMult(tier: ChampionTier, stars: StarLevel | 4): number {
  const mults: Record<ChampionTier, [number, number, number, number]> = {
    common:    [1, 1.5, 2.0, 2.8],
    rare:      [1, 1.7, 2.6, 3.6],
    epic:      [1, 2.0, 3.2, 4.5],
    legendary: [1, 2.0, 3.8, 5.3],
  };
  return (mults[tier] ?? mults.common)[stars - 1] ?? 1;
}

export function buildPlayerUnit(entry: RosterEntry, slot: number, level: number): Unit {
  const champ = CHAMPIONS[entry.championId as ChampionId];
  const b = entry.bonuses ?? { hpMult: 1, adMult: 1 };
  const starMult = getStarMult(champ.tier, entry.stars ?? 1);
  const itemDef = entry.itemId ? ITEMS[entry.itemId] : null;
  const cooldownMult = itemDef?.cooldownMult ?? 1;
  const nasusBonus = entry.championId === "Nasus" ? (entry.nasusStacks ?? 0) : 0;
  const def: UnitKindDef = {
    displayName: champ.displayName,
    baseHp: Math.round(champ.baseHp * b.hpMult * starMult),
    baseAd: Math.round(champ.baseAd * b.adMult * starMult) + nasusBonus,
    baseAp: Math.round(champ.baseAp * b.adMult * starMult),
    range: champ.range,
    moveSpeed: champ.moveSpeed,
    baseAttackSpeed: champ.baseAttackSpeed,

    abilityId: champ.abilityId,
    abilityCooldown: Math.round(champ.abilityCooldown * cooldownMult),
    iconUrl: championIconUrl(champ.iconId),
    critChance: champ.critChance,
  };
  const unit: Unit = {
    id: nextUnitId("p"),
    team: "player",
    def,
    level,
    stars: entry.stars ?? 1,
    currentHp: 0,
    pos: slotCoord(slot, "player"),
    championDef: champ,
    ...freshCombatState(def.abilityCooldown),
    redBuff: entry.redBuff ?? false,
    blueBuff: entry.blueBuff ?? false,
    item: entry.itemId ?? null,
    bansheeActive: itemDef?.banshee === true,
  };
  const stats = effectiveStats(unit);
  unit.currentHp = entry.currentHp === null
    ? stats.maxHp
    : Math.min(stats.maxHp, Math.max(0, entry.currentHp));
  return unit;
}

/** Apply Yuumi's aura: the ally directly in front of Yuumi gets +AD/AP/HP scaling with Yuumi's stars
 *  (1★ +10%, 2★ +15%, 3★ +20%). HP scales the unit's currentHp proportionally so the buff
 *  doesn't show up as a partial-HP ally at fight start. */
export function applyYuumiAura(players: Unit[]): void {
  const yuumi = players.find(u => u.championDef?.yuumiAura && u.currentHp > 0);
  if (!yuumi) return;
  const bonded = pickYuumiBondAlly(yuumi, players);
  if (!bonded) return;
  const beforeMax = effectiveStats(bonded).maxHp;
  bonded.yuumiBoostMult = yuumiAuraMult(yuumi.stars);
  const afterMax = effectiveStats(bonded).maxHp;
  if (beforeMax > 0) {
    bonded.currentHp = Math.min(afterMax, Math.round(bonded.currentHp * (afterMax / beforeMax)));
  }
}

export function yuumiAuraMult(stars: number): number {
  if (stars >= 3) return 1.20;
  if (stars >= 2) return 1.15;
  return 1.10;
}

/** Pick the living ally Yuumi is bonded to: closest ally in front of her (smaller q),
 *  falling back to the closest ally behind if no one is in front. */
export function pickYuumiBondAlly(yuumi: Unit, allies: readonly Unit[]): Unit | null {
  const others = allies.filter(u => u.id !== yuumi.id && u.currentHp > 0);
  if (others.length === 0) return null;
  const inFront = others.filter(u => u.pos.q < yuumi.pos.q);
  if (inFront.length > 0) {
    return inFront.sort((a, b) => b.pos.q - a.pos.q)[0]; // largest q below Yuumi = directly in front
  }
  return others.sort((a, b) => a.pos.q - b.pos.q)[0];
}

export function buildEnemyUnit(enemyId: EnemyId, slot: number, level = 1, statMult = 1): Unit {
  const e = ENEMIES[enemyId];
  const def: UnitKindDef = {
    displayName: e.displayName,
    baseHp: Math.round(e.baseHp * statMult),
    baseAd: Math.round(e.baseAd * statMult),
    baseAp: Math.round(e.baseAp * statMult),
    range: e.range,
    moveSpeed: e.moveSpeed,
    baseAttackSpeed: e.baseAttackSpeed,

    abilityId: null,
    abilityCooldown: 0,
    iconUrl: e.iconPath,
  };
  const unit: Unit = {
    id: nextUnitId("e"),
    team: "enemy",
    def,
    level,
    stars: 1,
    currentHp: 0,
    pos: slotCoord(slot, "enemy"),
    ...freshCombatState(0),
  };
  unit.currentHp = effectiveStats(unit).maxHp;
  return unit;
}

export function buildChampionEnemyUnit(championId: ChampionId, slot: number, level: number, stars: StarLevel | 4 = 1, statMult = 1): Unit {
  const champ = CHAMPIONS[championId];
  const starMult = getStarMult(champ.tier, stars);
  const def: UnitKindDef = {
    displayName: champ.displayName,
    baseHp: Math.round(champ.baseHp * starMult * statMult),
    baseAd: Math.round(champ.baseAd * starMult * statMult),
    baseAp: Math.round(champ.baseAp * starMult * statMult),
    range: champ.range,
    moveSpeed: champ.moveSpeed,
    baseAttackSpeed: champ.baseAttackSpeed,

    abilityId: champ.abilityId,
    abilityCooldown: champ.abilityCooldown,
    iconUrl: championIconUrl(champ.iconId),
    critChance: champ.critChance,
  };
  const unit: Unit = {
    id: nextUnitId("e"),
    team: "enemy",
    def,
    level,
    stars,
    currentHp: 0,
    pos: slotCoord(slot, "enemy"),
    championDef: champ,
    ...freshCombatState(def.abilityCooldown),
  };
  unit.currentHp = effectiveStats(unit).maxHp;
  return unit;
}

export function buildTibbersUnit(pos: HexCoord, team: Team, level: number): Unit {
  const def: UnitKindDef = {
    displayName: "Tibbers",
    baseHp: 400,
    baseAd: 85,
    baseAp: 0,
    range: 2,
    moveSpeed: 0,
    baseAttackSpeed: 0.8,

    abilityId: null,
    abilityCooldown: 0,
    iconUrl: `${import.meta.env?.BASE_URL ?? '/'}images/tibbers.png`,
  };
  const unit: Unit = {
    id: nextUnitId("tibbers"),
    team,
    def,
    level,
    stars: 1,
    currentHp: 0,
    pos,
    ...freshCombatState(0),
  };
  unit.currentHp = effectiveStats(unit).maxHp;
  return unit;
}

export function buildYorickGhoulUnit(pos: HexCoord, team: Team, level: number): Unit {
  const def: UnitKindDef = {
    displayName: "Ghoul",
    baseHp: 1,
    baseAd: 38,
    baseAp: 0,
    range: 2,
    moveSpeed: 0,
    baseAttackSpeed: 1.4,

    abilityId: null,
    abilityCooldown: 0,
    iconUrl: `${import.meta.env?.BASE_URL ?? '/'}images/ghoul.png`,
  };
  const unit: Unit = {
    id: nextUnitId("ghoul"),
    team,
    def,
    level,
    stars: 1,
    currentHp: 0,
    pos,
    ...freshCombatState(0),
  };
  unit.currentHp = effectiveStats(unit).maxHp;
  return unit;
}

export function buildGhostUnit(entry: RosterEntry, slot: number, level: number): Unit {
  const champ = CHAMPIONS[entry.championId as ChampionId];
  const stars = entry.stars ?? 1;
  const starMult = getStarMult(champ.tier, stars);
  const def: UnitKindDef = {
    displayName: `Ghost ${champ.displayName}`,
    baseHp: Math.round(champ.baseHp * starMult),
    baseAd: Math.round(champ.baseAd * starMult),
    baseAp: Math.round(champ.baseAp * starMult),
    range: champ.range,
    moveSpeed: champ.moveSpeed,
    baseAttackSpeed: champ.baseAttackSpeed,

    abilityId: champ.abilityId,
    abilityCooldown: champ.abilityCooldown,
    iconUrl: championIconUrl(champ.iconId),
  };
  const unit: Unit = {
    id: nextUnitId("ghost"),
    team: "enemy",
    def,
    level,
    stars,
    currentHp: 0,
    pos: slotCoord(slot, "enemy"),
    championDef: champ,
    ...freshCombatState(def.abilityCooldown),
  };
  unit.currentHp = effectiveStats(unit).maxHp;
  return unit;
}

// Finds first unoccupied slot for a team among already-placed units.
export function nextFreeSlot(existing: readonly Unit[], team: Team): number {
  const occupied = new Set(
    existing.filter(u => u.team === team).map(u => u.pos.q),
  );
  for (let s = 0; s < 5; s++) {
    if (!occupied.has(s)) return s;
  }
  return 4;
}
